import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchSnapshots } from "../lib/api";
import { useAuth } from "./AuthContext";

const SnapshotContext = createContext(null);

export function SnapshotProvider({ children }) {
  const { user, isSuperadmin } = useAuth();
  const isAdmin = user?.role === "admin";
  const isPrivileged = isSuperadmin || isAdmin;

  const [allSnapshots, setAllSnapshots] = useState([]);
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);
  const [selectedId, setSelectedId] = useState(null); // null = último
  const [selectedPais, setSelectedPais] = useState(null); // null = todos (solo superadmin)

  // isGlobal: superadmin con "Todos los equipos" OR admin (siempre ve su país completo)
  const isGlobal =
    (isSuperadmin && selectedCoordinator === "__all__") || isAdmin;

  const refresh = useCallback(() => {
    fetchSnapshots()
      .then(setAllSnapshots)
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Extract unique coordinators from snapshots (for superadmin selector)
  const coordinators = allSnapshots.reduce((acc, s) => {
    if (s.coordenador && !acc.find((c) => c.name === s.coordenador)) {
      acc.push({ name: s.coordenador, id: s.coordenador_id, pais: s.pais });
    }
    return acc;
  }, []);

  // Initialize selected coordinator
  useEffect(() => {
    if (allSnapshots.length > 0 && !selectedCoordinator) {
      if (isSuperadmin) {
        // Superadmin arranca en vista global
        setSelectedCoordinator("__all__");
      } else {
        setSelectedCoordinator(allSnapshots[0].coordenador);
      }
    }
  }, [allSnapshots, selectedCoordinator, isSuperadmin]);

  // Filter snapshots by coordinator for superadmin
  const snapshots = isGlobal
    ? []
    : (isPrivileged && selectedCoordinator
        ? allSnapshots.filter((s) => s.coordenador === selectedCoordinator)
        : allSnapshots);

  const selected = isGlobal
    ? null
    : (snapshots.find((s) => s.id === selectedId) || snapshots[0] || null);

  const changeCoordinator = (coordName) => {
    setSelectedCoordinator(coordName);
    if (coordName === "__all__") {
      setSelectedId(null);
    } else {
      const firstForCoord = allSnapshots.find((s) => s.coordenador === coordName);
      setSelectedId(firstForCoord ? firstForCoord.id : null);
    }
  };

  const changePais = (pais) => {
    setSelectedPais(pais);
    // Al cambiar país, volver a vista global
    setSelectedCoordinator("__all__");
    setSelectedId(null);
  };

  return (
    <SnapshotContext.Provider
      value={{
        snapshots,
        allSnapshots,
        selectedId,
        setSelectedId,
        selected,
        selectedCoordinator,
        setSelectedCoordinator: changeCoordinator,
        coordinators,
        refresh,
        isGlobal,
        selectedPais,
        setSelectedPais: changePais,
        isAdmin,
      }}
    >
      {children}
    </SnapshotContext.Provider>
  );
}

export function useSnapshot() {
  return useContext(SnapshotContext);
}
